import React from 'react';
import _ from 'underscore';
import SketchPicker from 'react-color/lib/Sketch';
import Breadcrumbs from '../components/Breadcrumbs';
import ClientHeader from '../components/ClientHeader';
import Profile from '../components/Profile';
import { PagePath } from '../components/GoToPage';
import ButtonUpload from '../components/Form/ButtonUpload';
import Label from '../components/Form/Label';
import ButtonWithIcon from '../components/Form/ButtonWithIcon';
import { Features } from '../components/Misc';

// Redux
import { mapProps, compose, withProps, withHandlers } from 'recompose';
import { connect } from 'react-redux';

// ACTIONS
import { startLoading, stopLoading } from '../redux/actions/PageAction';
import { setClient, actionUpdateClient, refreshClientLogo } from '../redux/actions/ClientAction';

// Misc
import { withFormUtils } from '../components/Misc/forms';
import { showSuccessfulMessage } from '../components/Misc/forms/popUps';
import {
    withTokenCheckWithRedirect,
    withForbiddenChecking,
} from '../components/Misc/authentication';
import { withClient } from '../components/Misc/client';
import { setValue } from '../redux/actions/ValueAction';

// receiving from reducer
const mapStateToProps = state => {
    const { fetching, error } = state.client;
    return {
        client: state.client || {},
        fetching,
        error,
    };
};

// dispatch actions
const mapDispatchToProps = dispatch => {
    return {
        startLoading() {
            dispatch(startLoading());
        },
        stopLoading() {
            dispatch(stopLoading());
        },
    };
};

const settingBreadcrumbs = withProps(({ features = [] }) => {
    const feature = features.find((e = {}) => e.type === Features.themeManagement) || {};
    return {
        breadcrumbs: [
            { title: 'Home', link: PagePath.dashboard },
            { title: feature.name || 'Theme Management', link: '#' },
        ],
    };
});

const PresentationalTable = ({
    breadcrumbs,
    isLoggedIn,
    isForbidden,
    Forbidden,
    client = {},

    handleSubmit,
    handleLogoUpload,
    handleColorChange,
    resetToSystemScheme,
}) => {
    if (isForbidden) return Forbidden;
    if (!isLoggedIn) return <span />;

    let value;
    if (client.logo) {
        value = [
            {
                path: client.logo,
                name: extractFilename(client.logo),
            },
        ];
    }
    return (
        <div className="container content template">
            <div className="profile__client">
                <ClientHeader />
                <Profile />
            </div>
            <Breadcrumbs option={breadcrumbs} />
            <div className="client-settings">
                <div className="settings-logo">
                    <ButtonUpload
                        id="logo"
                        isLogo={true}
                        showDownloadButton={false}
                        uploadConfigs={{
                            accepted_files: [
                                {
                                    type: 'jpg',
                                    size: 2,
                                },
                                {
                                    type: 'png',
                                    size: 2,
                                },
                            ],
                        }}
                        label={{
                            title: 'Logo',
                        }}
                        value={value}
                        onChange={handleLogoUpload}
                    />
                </div>
                <div className=""></div>
            </div>
            <div className="client-settings">
                <div className="settings-color">
                    <div className="color-picker">
                        <Label title="Primary Color" />
                        <br />
                        <SketchPicker
                            className="primary"
                            onChangeComplete={color => {
                                handleColorChange(color, 1);
                            }}
                            color={client.primaryColor}
                            disableAlpha
                        />
                    </div>
                    <div className="color-picker">
                        <Label title="Secondary Color" />
                        <br />
                        <SketchPicker
                            className="secondary"
                            onChangeComplete={color => {
                                handleColorChange(color, 2);
                            }}
                            color={client.secondaryColor}
                            disableAlpha
                        />
                    </div>
                </div>
                <div className="settings-submit">
                    <button
                        className="filter__button--underlined default"
                        onClick={resetToSystemScheme}>
                        System Scheme
                    </button>
                    <ButtonWithIcon title="Save" id="submit-settings" onClick={handleSubmit} />
                </div>
            </div>
        </div>
    );
};

const settingsTableDataHOC = compose(connect(mapStateToProps, mapDispatchToProps), withFormUtils);

const Settings = compose(
    connect(({ user, value }) => {
        return {
            features: user.features,
            values: value.values,
        };
    }),
    settingBreadcrumbs,
    settingsTableDataHOC,
    mapProps(
        ({ breadcrumbs, tableActions, columnActions, tableData, features, values, dispatch }) => ({
            breadcrumbs,
            columnActions,
            tableActions,
            tableData,
            features,
            values,
            dispatch,
        }),
    ),
    withForbiddenChecking,
    withProps(({ features }) => {
        let isForbidden = false;
        if (features) {
            const featureTypes = features.map(f => f.type);
            isForbidden = !_.includes(featureTypes, 'theme_management');
        }
        return {
            isForbidden,
        };
    }),
    withTokenCheckWithRedirect,
    withClient,
    withHandlers({
        resetToSystemScheme:
            ({ client, dispatch }) =>
            () => {
                client = {
                    ...client,
                    primaryColor: '',
                    secondaryColor: '',
                };
                dispatch(setClient(client));
            },
        handleLogoUpload:
            ({ client, dispatch }) =>
            files => {
                const fullPath = files && files[0] ? files[0].full_path : '';
                const path = files && files[0] ? files[0].path : '';
                dispatch(
                    setClient({
                        ...client,
                        logo: fullPath,
                        logoPath: path,
                    }),
                );

                // set new name
                dispatch(
                    setValue({
                        path: 'logo.0.name',
                        value: extractFilename(path),
                    }),
                );

                if (!path || !fullPath) return;
                console.log(fullPath, path);
                refreshClientLogo(fullPath);
                dispatch(showSuccessfulMessage());
            },
        handleColorChange:
            ({ client, dispatch }) =>
            (color, sequence) => {
                if (sequence === 1) {
                    dispatch(
                        setClient({
                            ...client,
                            primaryColor: color.hex,
                        }),
                    );
                } else {
                    dispatch(
                        setClient({
                            ...client,
                            secondaryColor: color.hex,
                        }),
                    );
                }
            },
        handleSubmit:
            ({ client, dispatch }) =>
            async () => {
                await dispatch(
                    actionUpdateClient({
                        primaryColor: client.primaryColor,
                        secondaryColor: client.secondaryColor,
                    }),
                );
                dispatch(showSuccessfulMessage());
            },
    }),
)(PresentationalTable);

export default Settings;

function extractFilename(path) {
    const matched = path.match(/[\w\-\_]*\.(jpg|jpeg|png)$/);
    return matched ? matched[0] : path;
}
